import { legalServiceSchema } from "@/lib/firm";
import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";
import { PROBATE_FEES, PROBATE_LOCATIONS } from "@/lib/probate-locations";

const SITE = "https://coastsidelaw.com.au";
const URL = `${SITE}/executor-queensland`;

const TITLE = `Executor of a Queensland Estate — Fixed-Fee Probate ${PROBATE_FEES.grant}`;
const DESCRIPTION = `Named executor of a Queensland estate? Fixed-fee grant of probate ${PROBATE_FEES.grant} incl. GST, full administration ${PROBATE_FEES.admin}. Interstate and Sydney executors handled remotely. Call ${FIRM.phone}.`;

const FAQS = [
  {
    q: "I have just been told I am the executor. What do I actually have to do?",
    a: "Locate the original will, obtain the death certificate, identify and secure the assets, work out whether a grant of probate is needed, apply for it if it is, pay the debts and tax, then distribute what remains and account to the beneficiaries. You are personally answerable for getting that sequence right, which is the main reason executors instruct a solicitor.",
  },
  {
    q: "Do I need a grant of probate at all?",
    a: "Not always. Assets held as joint tenants pass by survivorship, and superannuation and life insurance often sit outside the estate. Where there is real property in Queensland in the deceased's sole name, or a bank or share holding above the institution's threshold, a grant will be required. We tell you which of those you are in before you spend anything.",
  },
  {
    q: "Can I act as executor from Sydney or interstate?",
    a: "Yes. Residence has nothing to do with your authority — the will appoints you, not your postcode. The application is made in the Supreme Court of Queensland because the assets are here, and the whole file can run by email, phone and electronic signing. You do not need to travel to Queensland.",
  },
  {
    q: "The deceased died overseas — does that change anything?",
    a: "It changes the evidence, not the pathway. Where a foreign grant exists we can usually reseal it; where the person died overseas without one, we apply for an original grant in Queensland supported by evidence of death and, if only a copy of the will survives, an application to prove the copy.",
  },
  {
    q: "What does it cost?",
    a: `Our fixed professional fee for a straightforward grant is ${PROBATE_FEES.grant} including GST and full estate administration is ${PROBATE_FEES.admin} including GST. The Supreme Court filing fee and the cost of publishing the required notice are set by others and charged at cost. No percentage of the estate.`,
  },
  {
    q: "When is it safe to distribute?",
    a: "Not within six months of the date of death without accepting real risk — a family provision application can still be brought, and an executor who has already paid the money out can be left personally carrying it. We will tell you when it is safe and what to do if a claim is foreshadowed.",
  },
  {
    q: "Do I have to attend an office?",
    a: `No. Most executors deal with us entirely by email and phone. Our offices at ${FIRM_ADDRESS} and ${PENINSULA_ADDRESS} are available by appointment if you would rather sit down with someone.`,
  },
];

export const Route = createFileRoute("/executor-queensland")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${FIRM.name} — solicitors for executors, Queensland`,
          description: DESCRIPTION,
          telephone: FIRM.phone,
          address: legalServiceSchema.address,
          parentOrganization: { "@id": "https://coastsidelaw.com.au/#legalservice" },
          areaServed: [
            { "@type": "State", name: "Queensland" },
            { "@type": "State", name: "New South Wales" },
            { "@type": "Country", name: "Australia" },
          ],
          serviceType: [
            "Grant of probate",
            "Letters of administration",
            "Resealing a foreign or interstate grant",
            "Deceased estate administration",
            "Transmission application",
          ],
          offers: [
            {
              "@type": "Offer",
              name: "Grant of probate",
              price: PROBATE_FEES.grant.replace("$", "").replace(",", ""),
              priceCurrency: "AUD",
            },
            {
              "@type": "Offer",
              name: "Full estate administration",
              price: PROBATE_FEES.admin.replace("$", "").replace(",", ""),
              priceCurrency: "AUD",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Probate", item: `${SITE}/probate` },
            { "@type": "ListItem", position: 3, name: "Executors", item: URL },
          ],
        }),
      },
    ],
  }),
  component: ExecutorLanding,
});

const DUTIES = [
  {
    t: "1. Secure the will and the assets",
    d: "The original will, not a scan, is what the Court needs. Bank accounts, insurance and the property need to be notified and protected — including making sure the house is still insured while it sits empty.",
  },
  {
    t: "2. Work out whether a grant is needed",
    d: "We check how the property is held and what each institution will release without a grant. If you do not need one, we say so rather than sell you an application.",
  },
  {
    t: "3. Notice, filing and the grant",
    d: "Notice of intention to apply published and served on the Public Trustee, the application and your executor's affidavit prepared and filed in the Supreme Court of Queensland, and any registry requisitions answered.",
  },
  {
    t: "4. Collect, pay, distribute and account",
    d: "Assets called in, debts and tax dealt with, the transmission application and any sale of Queensland property handled in-house, then distribution and a set of estate accounts for the beneficiaries.",
  },
];

const RISKS = [
  {
    t: "Distributing too early",
    d: "Pay the beneficiaries inside six months and a later family provision claim can land on you personally. The timing rules exist for your protection — use them.",
  },
  {
    t: "Relying on a copy of the will",
    d: "Where the original cannot be found there is a presumption it was revoked. That is rebuttable, but it takes evidence and an application. Never staple, unstaple or annotate an original will.",
  },
  {
    t: "Missing an asset held outside the estate",
    d: "Superannuation and life insurance are usually paid at the trustee's discretion unless a valid binding nomination exists. Assuming the will controls them is a common and expensive mistake.",
  },
  {
    t: "Leaving the estate exposed",
    d: "Vacant homes, lapsed insurance, unlodged tax returns and unpaid rates all sit at the executor's door. We diarise them so the estate is not carrying avoidable losses.",
  },
];

function ExecutorLanding() {
  return (
    <>
      <PageHero
        eyebrow="For executors"
        title="You have been appointed executor of a Queensland estate"
        lead={`Fixed fee ${PROBATE_FEES.grant} including GST for a grant of probate, ${PROBATE_FEES.admin} for the whole administration. Wherever you live, the file runs by email and phone.`}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Grant of probate
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{PROBATE_FEES.grant}</p>
            <p className="mt-1 text-sm text-muted-foreground">including GST</p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Full estate administration
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{PROBATE_FEES.admin}</p>
            <p className="mt-1 text-sm text-muted-foreground">including GST</p>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Court filing fees and the cost of the published notice are set by others and charged at
          cost. No percentage of the estate, and the fee is confirmed in writing before you engage
          us. The estate normally pays it.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">What an executor has to do</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {DUTIES.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          Where executors get personally caught
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {RISKS.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border border-primary/40 bg-primary/5 p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Sydney and interstate executors
          </p>
          <h2 className="mt-3 font-serif text-3xl text-foreground">
            Living in Sydney with a Queensland estate to administer?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            This is a large part of what we do. The house at Caloundra, the unit on the Redcliffe
            peninsula, the Queensland bank account and the share parcel all have to be dealt with
            under Queensland law and through the Supreme Court of Queensland — but you do not have
            to be here for any of it. Identity verification, signing and witnessing are arranged
            where you live, the affidavit material comes to you by email, and settlement of any
            Queensland property is electronic.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            If a New South Wales or overseas grant has already issued, resealing in Queensland is
            usually the faster route. If it has not, we apply here directly. Send us the will and a
            list of assets and you will get a plain answer on which pathway applies, what it costs
            and how long it takes — before you commit to anything.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
              className="inline-block rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Call {FIRM.phone}
            </a>
            <Link
              to="/remote-probate"
              className="inline-block rounded-sm border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-primary"
            >
              How remote probate works
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who will handle it" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Probate by area</h2>
        <ul className="mt-5 flex flex-wrap gap-2">
          {PROBATE_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/probate/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Probate {l.name}
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Related pages</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/probate" className="font-semibold text-primary underline">
              Probate and estate administration
            </Link>
          </li>
          <li>
            <Link to="/estate-disputes" className="font-semibold text-primary underline">
              Estate disputes and family provision claims
            </Link>
          </li>
          <li>
            <Link to="/wills-and-estates" className="font-semibold text-primary underline">
              Wills, estates and elder law
            </Link>
          </li>
        </ul>

        <div className="mt-14">
          <ExecutorIntakeForm heading="Executor intake — tell us about the estate" />
        </div>
      </section>

      <CtaBand
        title="Send us the will and we will tell you what is needed"
        body={`Call ${FIRM.phone} or send the will and a list of assets through. We will confirm whether a grant is required and give you the fee in writing before you commit.`}
      />
      <div className="h-20" />
    </>
  );
}
